/**
 * Remembers the parent who signed in on this device, so that after switching
 * into a child account we can offer a way back.
 *
 * The API never lets a child reach the parent account, on purpose. Getting
 * back up is done here instead, with the parent's own token, saved the moment
 * they signed in with their own password. That keeps a leaked child password
 * from reaching the parent.
 *
 * Stored in a cookie, mirrored to localStorage because the Capacitor WebView
 * is less dependable about cookies than a browser is.
 */

const COOKIE_NAME = 'rq_parent';
const MIRROR_KEY = 'rq_parent';
const MAX_AGE_DAYS = 30;

function isSecureContext(){
    return typeof window !== 'undefined' && window.location && window.location.protocol === 'https:';
}

function writeCookie(value, maxAgeSeconds){
    if (typeof document === 'undefined') return;

    let cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
    if (isSecureContext()){
        cookie += '; Secure';
    }
    document.cookie = cookie;
}

function readCookie(){
    if (typeof document === 'undefined') return null;

    const match = document.cookie.split('; ').find(row => row.indexOf(`${COOKIE_NAME}=`) === 0);
    return match ? match.substring(COOKIE_NAME.length + 1) : null;
}

/**
 * Save the signed-in parent so we can come back to them later.
 * @param {{id: number, name: string}} user
 * @param {string} token
 */
function encode(value){
    // base64 of the utf-8 bytes, so a name with Arabic letters survives the
    // trip through a cookie
    return window.btoa(unescape(encodeURIComponent(JSON.stringify(value))));
}

function decode(payload){
    return JSON.parse(decodeURIComponent(escape(window.atob(payload))));
}

function remember(user, token){
    if (!user || !token) return;

    const payload = encode({
        id: user.id,
        name: user.name,
        token,
    });

    writeCookie(payload, MAX_AGE_DAYS * 24 * 60 * 60);

    try {
        window.localStorage.setItem(MIRROR_KEY, payload);
    } catch (e) {
        // storage unavailable, the cookie still carries it
    }
}

/**
 * The remembered parent, or null.
 * @returns {{id: number, name: string, token: string}|null}
 */
function get(){
    let payload = readCookie();

    if (!payload){
        try {
            payload = window.localStorage.getItem(MIRROR_KEY);
        } catch (e) {
            payload = null;
        }
    }

    if (!payload) return null;

    try {
        const parsed = decode(payload);
        return (parsed && parsed.id && parsed.token) ? parsed : null;
    } catch (e) {
        return null;
    }
}

function forget(){
    writeCookie('', 0);

    try {
        window.localStorage.removeItem(MIRROR_KEY);
    } catch (e) {
        // nothing to clean up
    }
}

/**
 * Called on every sign in. A parent is remembered, anyone else clears whatever
 * the last person on this device left behind, so a child signing in on a shared
 * computer is never offered a way into someone else's account.
 */
function rememberIfParent(user, token){
    if (user && user.has_childs){
        remember(user, token);
    }else{
        forget();
    }
}

/**
 * Whether we are currently looking at someone other than the remembered parent.
 */
function isSwitchedAway(currentUser){
    const parent = get();
    return !!(parent && currentUser && parent.id !== currentUser.id);
}

export default {
    remember,
    rememberIfParent,
    get,
    forget,
    isSwitchedAway,
};
